import * as React from "react";

import { cn } from "@/shared/utils/utils";
import type {
  FormikSkeletonSection,
  FormikSkeletonProps,
} from "@/shared/type/skeleton.type";
import { Skeleton } from "./skeleton";

const defaultFormikSkeletonSections: FormikSkeletonSection[] = [
  {
    title: "Form details",
    fieldRows: [
      [{ span: 1 }, { span: 1 }, { span: 1 }],
      [{ span: 1 }, { span: 1 }, { span: 1 }],
      [{ span: 2 }, { kind: "switch", span: 1 }],
      [
        { kind: "textarea", span: 1 },
        { kind: "textarea", span: 1 },
      ],
    ],
  },
];

export const FormikSkeleton = ({
  className,
  sections = defaultFormikSkeletonSections,
  ...props
}: FormikSkeletonProps): React.ReactElement => {
  return (
    <div
      aria-busy="true"
      aria-label="Loading form"
      data-slot="formik-skeleton"
      className={cn("space-y-4", className)}
      {...props}
    >
      <span className="sr-only" role="status">
        Loading form
      </span>

      {sections.map((section) => {
        const fieldRows = section.fieldRows ?? [];
        const rowCount = Math.max(1, section.rowCount ?? 3);
        const tableColumnCount = Math.max(2, section.tableColumnCount ?? 4);
        const rowIndexes = new Array(rowCount)
          .fill(0)
          .map((value, index) => value + index);
        const columnIndexes = new Array(tableColumnCount)
          .fill(0)
          .map((value, index) => value + index);

        return (
          <section
            key={section.title}
            className="my-4 overflow-hidden rounded-20 bg-white"
          >
            <div className="flex items-center justify-between p-4">
              <Skeleton className="h-6 w-36" />
              <Skeleton className="size-5 rounded-full" />
            </div>

            {section.kind !== "table" && (
              <div className="flex w-full flex-col gap-4 border-t border-border p-4">
                {fieldRows.map((fields, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="-mx-2.5 flex flex-wrap items-start gap-y-4"
                  >
                    {fields.map((field, fieldIndex) => {
                      const span = field.span ?? 1;
                      const widthClass =
                        span === 3
                          ? "xmd:basis-full xmd:max-w-full"
                          : span === 2
                            ? "xmd:basis-2/3 xmd:max-w-2/3"
                            : "xmd:basis-1/3 xmd:max-w-1/3";

                      return (
                        <div
                          key={fieldIndex}
                          className={cn(
                            "basis-full max-w-full px-2.5",
                            widthClass,
                            field.className,
                          )}
                        >
                          {field.kind === "switch" ? (
                            <div className="flex flex-col gap-1.5">
                              <Skeleton
                                className={cn("h-4 w-14", field.labelClassName)}
                              />
                              <Skeleton className="h-6 w-11 rounded-full" />
                            </div>
                          ) : field.kind === "checkbox" ? (
                            <div className="flex items-center gap-2 pt-5">
                              <Skeleton className="size-4 rounded-sm" />
                              <Skeleton
                                className={cn("h-4 w-28", field.labelClassName)}
                              />
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <Skeleton
                                className={cn("h-4 w-24", field.labelClassName)}
                              />
                              <Skeleton
                                className={cn(
                                  field.kind === "textarea"
                                    ? "h-[76px] w-full"
                                    : "h-10 w-full",
                                )}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}

                {section.logo && (
                  <div className="flex flex-col gap-3">
                    <Skeleton className="h-5 w-32" />
                    <div className="-mx-2.5 flex flex-wrap items-start gap-y-4">
                      {[0, 1].map((logoIndex) => (
                        <div
                          key={logoIndex}
                          className="basis-full max-w-full px-2.5 xmd:basis-1/2 xmd:max-w-1/2"
                        >
                          <div className="flex flex-col gap-3">
                            <Skeleton className="h-4 w-28" />
                            <Skeleton className="h-50 w-full rounded-20" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {section.kind === "table" && (
              <div className="overflow-hidden border-t border-border p-4">
                <div className="flex gap-3 border-b border-border pb-3">
                  {columnIndexes.map((columnIndex) => (
                    <Skeleton
                      key={columnIndex}
                      className="h-4 min-w-20 flex-1"
                    />
                  ))}
                </div>
                <div className="space-y-3 pt-3">
                  {rowIndexes.map((rowIndex) => (
                    <div key={rowIndex} className="flex gap-3">
                      {columnIndexes.map((columnIndex) => (
                        <Skeleton
                          key={columnIndex}
                          className="h-10 min-w-20 flex-1"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
